import { Card, CardContent, CardHeader, CardTitle } from "~/components/reusable/Card"
import { Storefront } from "@phosphor-icons/react"
import { api } from "~/utils/api"
import { type ICollectionDetails } from "~/hooks/useCollections"
import { filterNotUndefinedOrNull } from "~/utils/tsUtils"
import SuperChart, { type PieChartData } from "~/components/reusable/graphs/SuperChart"
import LoadingOverlay from "~/components/reusable/LoadingOverlay"
import { ChartType, LegendAlignment } from "~/constants/graphs"
import { useHakiChartColors } from "~/constants/colors"
import { formatName } from "~/utils/namingUtils"

type Props = {
  collections: ICollectionDetails[]
  startDate: Date
  endDate: Date
  warehouseId?: string
  channel?: string
}

const TopCustomersPieChartCard = ({
  collections,
  startDate,
  endDate,
  warehouseId,
  channel,
}: Props) => {
  const hakiColors = useHakiChartColors()
  const { data: topCustomers, isLoading } = api.preOrder.getTopCustomersForCollection.useQuery({
    collectionIds: collections.map((c) => c.id),
    externalCollectionIds: filterNotUndefinedOrNull(collections.map((c) => c.externalId)),
    startDate,
    endDate,
    warehouseId,
    channel,
  })

  const data: PieChartData[] =
    topCustomers?.map((t, i) => ({
      name: t.name,
      data: t.value,
      color: hakiColors[i % hakiColors.length] ?? "",
      custom: {
        legendTooltip: t.name,
        truncate: true,
      },
    })) ?? []

  return (
    <Card className="w-full">
      <CardHeader className="justify-start gap-2">
        <Storefront />
        <CardTitle>Key customers</CardTitle>
      </CardHeader>
      <CardContent className="flex items-center justify-center h-[314px]">
        {isLoading && <LoadingOverlay />}

        {!isLoading && (!topCustomers || topCustomers.length === 0) && (
          <div className="text-center label-lg">
            <p>There have been made no {formatName("preorder", true)} by customers.</p>
          </div>
        )}

        {!isLoading && topCustomers !== undefined && topCustomers.length > 0 && (
          <SuperChart
            type={ChartType.Pie}
            height={270}
            data={data}
            legendAlignment={LegendAlignment.Right}
            subtitleLabel={formatName("preorder", true, true)}
          />
        )}
      </CardContent>
    </Card>
  )
}

export default TopCustomersPieChartCard
